import { ApolloClient, ApolloLink, ApolloProvider } from '@apollo/client';
import { HttpLink, InMemoryCache, gql } from '@apollo/client/core';
import { setContext } from '@apollo/client/link/context';
import * as Sentry from '@sentry/react';
import { SentryLink } from 'apollo-link-sentry';
import { useEffect, useRef } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';

import { useAuthTokenContext } from 'support/auth/AuthTokenContext';
import { QueryParams } from 'support/QueryParams';
import { useSessionStorage } from 'support/react/hooks';
import generatedIntrospection from 'types/graphql';

const cache = new InMemoryCache({
  // For union fragment matching
  // See: https://the-guild.dev/graphql/codegen/plugins/other/fragment-matcher
  possibleTypes: generatedIntrospection.possibleTypes,

  // The Team type has two denormalized relations, theme and features. Since these relations are one to one,
  // we can safely assume that properties across queries can be merged into one object. Apollo does not make this
  // assumption about denromalized relations and as such would invalidate cache entries for every new property queried on theme or features.
  typePolicies: {
    Organization: {
      fields: {
        theme: {
          merge: true,
        },
        features: {
          merge: true,
        },
      },
    },
    Team: {
      fields: {
        theme: {
          merge: true,
        },
        features: {
          merge: true,
        },
      },
    },
    Track: {
      fields: {
        lab_config: {
          merge: true,
        },
      },
    },
    User: {
      fields: {
        profile: {
          merge: true,
        },
      },
    },
  },
});

const httpLink = new HttpLink({
  uri: '/graphql',
});

const traceparentLink = setContext((_, { headers }) => {
  const span = Sentry.getCurrentHub().getScope().getSpan();

  if (!span) {
    return {};
  }

  const traceId = span.traceId;
  const spanId = span.spanId;

  return {
    headers: {
      ...headers,
      // sentry's traceparent is not compatible with the w3c standard
      traceparent: `00-${traceId}-${spanId}-01`,
    },
  };
});

const apolloClient = new ApolloClient({
  cache,
  connectToDevTools: true,
  resolvers: {
    Mutation: {
      updateLocalTeam: (_, { id, team }, { cache }) => {
        cache.writeFragment({
          id: cache.identify({
            __typename: 'Team',
            id,
          }),
          fragment: gql`
            fragment BrandedTeam on Team {
              name
              logo
              title
              subtitle
              slug

              primary_link_text
              primary_link_url
              secondary_link_text
              secondary_link_url

              theme {
                foreground_color
                foreground_image
                background_color
                background_image
                primary_foreground_color
                primary_background_color
                secondary_foreground_color
                secondary_background_color
              }
            }
          `,
          data: team,
        });

        return team;
      },
    },
  },
});

export const GraphQLProvider = ({ children }: { children: React.ReactNode }) => {
  const { token, tokenID } = useAuthTokenContext();
  const embedToken = useEmbedToken();

  /**
   * We use refs to prevent recreating links
   *
   * Using effects/memos creates timing issues
   * where queries may fire before the link has
   * been recreated with the updated token
   */
  const tokenRef = useRef(token);
  const embedTokenRef = useRef(embedToken);

  // Keep up to date on every render
  tokenRef.current = token;
  embedTokenRef.current = embedToken;

  useEffect(() => {
    // Clear apollo cache when the authenticated user changes
    cache.reset();
  }, [tokenID]);

  useEffect(() => {
    /**
     * This Apollo link adds a header to the HTTP request containing the embed token, if any.
     *
     * The embed token can be passed as a query parameter to the splash screen, which stores the token
     * in the session storage. This token allows users to play private or unpublished content.
     *
     * We decided to pass the token as a HTTP header, fetched from the session storage, to avoid altering
     * every query/mutation used by the learn environment with a embed_token argument.
     */
    const embedTokenLink = setContext((_, { headers }) => {
      if (!embedTokenRef.current) {
        return { headers };
      }

      return {
        headers: {
          ...headers,
          'X-Embed-Token': embedTokenRef.current,
        },
      };
    });

    const authLink = setContext((request, { headers }) => {
      if (!tokenRef.current) {
        Sentry.captureMessage('Unauthenticated request', {
          extra: {
            operationName: request.operationName,
            variables: request.variables,
          },
        });

        return { headers };
      }

      return {
        headers: {
          ...headers,
          Authorization: `Bearer ${tokenRef.current}`,
        },
      };
    });

    apolloClient.setLink(
      ApolloLink.from([
        new SentryLink({ attachBreadcrumbs: { includeError: true } }),
        embedTokenLink,
        authLink,
        traceparentLink,
        httpLink,
      ]),
    );
  }, []);

  return <ApolloProvider client={apolloClient}>{children}</ApolloProvider>;
};

function useEmbedToken() {
  const [searchParams] = useSearchParams();
  const { trackSlug = '' } = useParams<'trackSlug'>();

  // We use the key slug as a unique key to the embed token for a track
  // this safeguards use cases where more than one embed track is used
  // by the user.
  const [storageEmbedToken, setStorageEmbedToken] = useSessionStorage<string>(`embedToken-${trackSlug}`);
  const embedToken = searchParams.get(QueryParams.EMBED_TOKEN) || storageEmbedToken;

  // Keep session storage up to date with the latest token
  useEffect(() => {
    setStorageEmbedToken(embedToken);
  }, [embedToken, setStorageEmbedToken]);

  return embedToken;
}
